import { Link } from "wouter";
import { ArrowRight, Tag, Calendar, Heart, Share2, MessageCircle, Linkedin, Facebook } from "lucide-react";
import type { BlogPost } from "@/hooks/useBlogPosts";
import { useBlogFavoriten } from "@/hooks/useBlogFavoriten";
import InstagramShareButton from "@/components/InstagramShareButton";
import { DEFAULT_SOCIAL_IMAGE } from "@shared/social-image";

function KategorieBadge({ kategorie }: { kategorie: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-[#2d1b4e]" style={{ backgroundColor: "#c9a84c" }}>
      <Tag size={10} />
      <span data-original-content>{kategorie}</span>
    </span>
  );
}

function DatumFormatiert({ datum }: { datum: string }) {
  const [day, month, year] = datum.split(".");
  const monate = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const monatsname = monate[parseInt(month, 10) - 1] ?? month;
  return (
    <span className="flex items-center gap-1 text-xs text-[#6b5c7e]">
      <Calendar size={11} />
      {day}. {monatsname} {year}
    </span>
  );
}

export function CompactShareIcons({ titel, slug, bild, teaser, kategorie, keywords }: { titel: string; slug: string; bild?: string; teaser?: string; kategorie?: string; keywords?: string }) {
  const url = encodeURIComponent(`https://immozwerg.com/blog/${slug}`);
  const text = encodeURIComponent(`Lesenswert: ${titel}`);

  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
      <InstagramShareButton
        post={{ titel, slug, bild, teaser, kategorie, keywords }}
        compact={true}
        title="Auf Instagram teilen"
      />
      <a
        href={`https://api.whatsapp.com/send?text=${text}%20${url}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf WhatsApp teilen"
        className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-[#25D366] hover:text-white hover:bg-[#25D366] transition-all bg-[#25D366]/10 border border-[#25D366]/30"
        title="Auf WhatsApp teilen"
      >
        <MessageCircle size={15} />
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf LinkedIn teilen"
        className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-[#0A66C2] hover:text-white hover:bg-[#0A66C2] transition-all bg-[#0A66C2]/10 border border-[#0A66C2]/30"
        title="Auf LinkedIn teilen"
      >
        <Linkedin size={15} />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Auf Facebook teilen"
        className="w-11 h-11 inline-flex items-center justify-center rounded-xl text-[#1877F2] hover:text-white hover:bg-[#1877F2] transition-all bg-[#1877F2]/10 border border-[#1877F2]/30"
        title="Auf Facebook teilen"
      >
        <Facebook size={15} />
      </a>
    </div>
  );
}

export default function BlogCard({ post }: { post: BlogPost }) {
  const { toggle, istFavorit } = useBlogFavoriten();
  const favorisiert = istFavorit(post.slug);

  return (
    <article className="bg-white rounded-2xl overflow-hidden border border-[#ede5f5] shadow-sm hover:shadow-lg hover:border-[#c9a0e0] transition-all duration-300 flex flex-col group">
      {/* Titelbild + Favorit-Herzchen */}
      <div className="relative h-48 overflow-hidden bg-[#ede5f5]">
        {post.bild ? (
          <img
            src={post.bild}
            alt={post.titel}
            data-original-content
            width="400"
            height="192"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full bg-[#2d1b4e]">
            <img
              src={DEFAULT_SOCIAL_IMAGE}
              alt="Neutraler Immozwerg Blog-Platzhalter"
              width="400"
              height="192"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <KategorieBadge kategorie={post.kategorie} />
        </div>
        <button
          onClick={(e) => { e.preventDefault(); toggle(post.slug); }}
          aria-label={favorisiert ? "Aus Favoriten entfernen" : "Als Favorit speichern"}
          className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-sm transition-all hover:scale-110"
          title={favorisiert ? "Aus Favoriten entfernen" : "Als Favorit speichern"}
        >
          <Heart size={16} className={favorisiert ? "fill-[#a066c2] text-[#a066c2]" : "text-[#b09ec0]"} />
        </button>
      </div>

      {/* Inhalt */}
      <div className="p-5 flex flex-col flex-1">
        <DatumFormatiert datum={post.datum} />
        <h2 className="text-lg font-bold text-[#2d1b4e] mt-2 mb-3 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
          <span data-original-content>{post.titel}</span>
        </h2>
        <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 flex-1">
          <span data-original-content>{post.teaser}</span>
        </p>
        <div className="mt-5 pt-4 border-t border-gray-50 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
          <Link href={`/blog/${post.slug}`} aria-label={`Artikel: ${post.titel}`}>
            <button
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-full text-xs font-bold text-white transition-all hover:scale-105 hover:shadow-md justify-center text-left leading-tight max-w-[180px]"
              style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}
            >
              <span className="truncate">Artikel: <span data-original-content>{post.titel}</span></span> <ArrowRight size={14} className="flex-shrink-0" />
            </button>
          </Link>
          <CompactShareIcons titel={post.titel} slug={post.slug} bild={post.bild} teaser={post.teaser} kategorie={post.kategorie} keywords={post.keywords} />
        </div>
      </div>
    </article>
  );
}
